import React, { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { FileUp, FileText, X, Download } from 'lucide-react';
import { useCertificationDocuments } from '@/hooks/useCertificationDocuments';

interface CertificationDocumentUploadProps {
  onFileSelect: (file: File) => void;
  existingDocumentUrl?: string;
  onRemove?: () => void;
  disabled?: boolean;
  required?: boolean;
}

export function CertificationDocumentUpload({
  onFileSelect,
  existingDocumentUrl,
  onRemove,
  disabled = false,
  required = false,
}: CertificationDocumentUploadProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const { validateFile, getDocumentUrl } = useCertificationDocuments();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (disabled) return;

    if (e.target.files && e.target.files[0]) {
      handleFileSelection(e.target.files[0]);
    }
  };

  const handleFileSelection = (file: File) => {
    const error = validateFile(file);
    if (!error) {
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const handleRemove = () => {
    setSelectedFile(null);
    if (onRemove) {
      onRemove();
    }
  };

  const handleDownload = async () => {
    if (!existingDocumentUrl) return;
    
    const signedUrl = await getDocumentUrl(existingDocumentUrl);
    if (signedUrl) {
      window.open(signedUrl, '_blank');
    }
  };

  return (
    <div className="space-y-2">
      <Label>
        Supporting Document {required ? <span className="text-destructive font-semibold">(Required)</span> : <span className="text-muted-foreground">(Optional)</span>}
      </Label>
      <p className="text-xs text-muted-foreground mb-2">
        Upload certificate, license, or proof of certification (PDF, JPG, PNG - Max 10MB)
      </p>

      {existingDocumentUrl && !selectedFile ? (
        <div className="border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <p className="text-sm font-medium">Document Uploaded</p>
                <p className="text-xs text-muted-foreground">
                  {existingDocumentUrl.split('/').pop()}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleDownload}
              >
                <Download className="h-4 w-4 mr-1" />
                View
              </Button>
              {!disabled && onRemove && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleRemove}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : selectedFile ? (
        <div className="border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-primary" />
              <div>
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            {!disabled && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={handleRemove}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !disabled && document.getElementById('cert-file-input')?.click()}
        >
          <FileUp className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm font-medium mb-1">
            Drop file here or click to upload
          </p>
          <p className="text-xs text-muted-foreground">
            PDF, JPG, or PNG (max 10MB)
          </p>
          <input
            id="cert-file-input"
            type="file"
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleChange}
            disabled={disabled}
            required={required}
          />
        </div>
      )}
    </div>
  );
}
